<template>
  <div>
    <div v-if="error" class="notice notice--danger" style="margin-bottom: var(--space-4)">
      <div class="notice__body">
        {{ error.text }}
        <ul v-if="error.list && error.list.length"><li v-for="(e, i) in error.list" :key="i">{{ e }}</li></ul>
      </div>
    </div>
    <div v-if="submittedNote" class="notice notice--success" style="margin-bottom: var(--space-4)">
      <div class="notice__body">{{ t("submit.submitted") }}</div>
    </div>

    <div class="card">
      <h2 class="card__title">{{ t("editor.versions") }}</h2>
      <p class="card__sub">{{ t("editor.versionsLede") }}</p>
      <div v-if="versions.length" class="table__wrap">
        <table class="table">
          <thead>
            <tr>
              <th>{{ t("apps.version") }}</th>
              <th>{{ t("common.status") }}</th>
              <th>{{ t("editor.scopes") }}</th>
              <th>{{ t("common.created") }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="v in versions" :key="v.id">
              <tr>
                <td class="mono">{{ v.version }}</td>
                <td><span :class="['badge', versionBadge(v.status)]">{{ versionLabel(v.status) }}</span></td>
                <td class="small muted">{{ (v.scopes || []).join(", ") }}</td>
                <td class="small muted">{{ formatDate(v.submitted_at || v.created_at || v.ctime) }}</td>
                <td>
                  <div class="row row--wrap">
                    <button v-if="v.status !== 'draft'" type="button" class="btn btn--ghost btn--sm"
                            :aria-pressed="selectedId === v.id" @click="selectedId = v.id">
                      {{ t("review.show") }}
                    </button>
                    <button v-if="submittable(v)" type="button" class="btn btn--sm"
                            :disabled="busy || blockers(v).length > 0" @click="submit(v)">
                      {{ t("editor.submit") }}
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="submittable(v) && blockers(v).length">
                <td colspan="5">
                  <p class="small strong">{{ t("submit.blockedTitle") }}</p>
                  <ul class="reasons">
                    <li v-for="b in blockers(v)" :key="b.key">
                      {{ b.text }}
                      <router-link v-if="b.to" :to="b.to">{{ b.action }}</router-link>
                      <button v-else-if="b.tab" type="button" class="auth__switch" @click="emit('goto', b.tab)">{{ b.action }}</button>
                    </li>
                  </ul>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <p v-else class="muted small">{{ t("apps.noVersion") }}</p>
    </div>

    <ReviewPanel v-if="selected" :key="`${selected.id}:${selected.status}`" :app-id="app.id" :version="selected" @settled="emit('changed')" />

    <div class="card">
      <h2 class="card__title">{{ t("editor.newVersion") }}</h2>
      <p class="card__sub">{{ t("editor.manifestLede") }}</p>

      <div class="grid grid--2" style="margin-top: var(--space-4)">
        <div class="field">
          <label class="field__label" for="v-version">{{ t("apps.version") }} <span class="req">*</span></label>
          <input id="v-version" v-model.trim="draft.version" class="input input--mono" placeholder="1.0.0" maxlength="32" />
        </div>
        <div></div>
        <div class="field">
          <label class="field__label" for="v-app">{{ t("editor.appUrl") }} <span class="req">*</span></label>
          <input id="v-app" v-model.trim="draft.app_url" class="input" placeholder="https://app.example.com/embed" />
          <span class="field__hint">{{ t("editor.appUrlHint") }}</span>
        </div>
        <div class="field">
          <label class="field__label" for="v-install">{{ t("editor.installUrl") }} <span class="req">*</span></label>
          <input id="v-install" v-model.trim="draft.install_url" class="input" placeholder="https://app.example.com/install" />
          <span class="field__hint">{{ t("editor.installUrlHint") }}</span>
        </div>
      </div>
      <div class="field">
        <label class="field__label" for="v-redirect">{{ t("editor.redirectUris") }} <span class="req">*</span></label>
        <textarea id="v-redirect" v-model="redirectText" class="textarea textarea--mono" rows="3"
                  placeholder="https://app.example.com/oauth/callback"></textarea>
        <span class="field__hint">{{ t("editor.redirectUrisHint") }}</span>
      </div>

      <h4 class="editor__group">{{ t("editor.scopes") }} <span class="req">*</span></h4>
      <p class="muted small">{{ t("editor.scopesLede", { min: MIN_JUSTIFICATION }) }}</p>
      <div v-for="scope in scopes" :key="scope.name" class="scope">
        <label class="scope__check">
          <input v-model="draft.scopes" type="checkbox" :value="scope.name" />
          <span>
            <strong>{{ scope.label || scope.name }}</strong>
            <code class="xs">{{ scope.name }}</code>
            <span v-if="scope.sensitive" class="badge badge--danger">{{ t("editor.sensitive") }}</span>
            <br />
            <span class="muted small">
              {{ scope.explanation }}
              <template v-if="scope.endpoints"> · {{ t("editor.unlocks", { count: scope.endpoints.length }) }}</template>
            </span>
          </span>
        </label>
        <template v-if="draft.scopes.includes(scope.name)">
          <textarea
            v-model="draft.scope_justifications[scope.name]"
            class="textarea"
            rows="2"
            :aria-label="t('editor.justification')"
            :placeholder="t('editor.justificationPlaceholder')"
          ></textarea>
          <span :class="['field__hint', { field__error: justificationShort(scope.name) }]">
            {{ t("editor.justificationCount", { count: justificationLength(scope.name), min: MIN_JUSTIFICATION }) }}
          </span>
        </template>
      </div>

      <h4 class="editor__group">{{ t("editor.deviceAccess") }}</h4>
      <p class="muted small">{{ t("editor.deviceAccessLede") }}</p>
      <div class="scope">
        <label class="scope__check">
          <input v-model="draft.capabilities" type="checkbox" value="microphone" />
          <span>
            <strong>{{ t("editor.microphone") }}</strong>
            <code class="xs">microphone</code>
            <br />
            <span class="muted small">{{ t("editor.microphoneHint") }}</span>
          </span>
        </label>
      </div>

      <h4 class="editor__group">{{ t("editor.events") }}</h4>
      <p class="muted small">{{ t("editor.eventsLede") }}</p>
      <textarea
        v-model="eventsText"
        class="textarea mono"
        rows="6"
        spellcheck="false"
        dir="ltr"
        :aria-label="t('editor.events')"
        placeholder='[{ "code": "pedestrian_ahead", "label": "Pedestrian ahead", "fields": [{ "key": "confidence", "type": "number" }] }]'
      ></textarea>
      <span v-if="eventsError" class="field__hint field__error">{{ eventsError }}</span>

      <div class="row" style="margin-top: var(--space-4)">
        <button class="btn" :disabled="busy || !canCreate" @click="createVersion">
          <span v-if="busy" class="spinner" aria-hidden="true"></span>
          {{ t("editor.createVersion") }}
        </button>
        <span v-if="!canCreate" class="small muted">{{ t("editor.createVersionHint") }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { api } from "../../lib/api";
import { session } from "../../lib/session";
import { useErrorText, formatDate } from "../../lib/errors";
import { listingGaps, weakJustifications, MIN_JUSTIFICATION } from "../../lib/listing";
import ReviewPanel from "./ReviewPanel.vue";

const props = defineProps({
  app: { type: Object, required: true },
  versions: { type: Array, default: () => [] },
  scopes: { type: Array, default: () => [] },
  listingEn: { type: Object, default: null }
});
const emit = defineEmits(["changed", "goto"]);
const { t, te } = useI18n();
const errorText = useErrorText();

const busy = ref(false);
const error = ref(null);
const submittedNote = ref(false);
const selectedId = ref(null);
const redirectText = ref("");
const eventsText = ref("");
const draft = reactive({ version: "", app_url: "", install_url: "", scopes: [], scope_justifications: {}, capabilities: [] });

const SUBMITTABLE = ["draft", "failed", "checks_failed", "rejected"];
const VERSION_BADGE = { submitted: "badge--info", checking: "badge--info", passed: "badge--success", live: "badge--success", failed: "badge--danger", checks_failed: "badge--danger" };

const selected = computed(() => props.versions.find((v) => v.id === selectedId.value) || null);

watch(
  () => props.versions,
  (list) => {
    if (!list.some((v) => v.id === selectedId.value)) {
      selectedId.value = (list.find((v) => v.status !== "draft") || {}).id || null;
    }
    const latest = list[0];
    if (latest && !draft.app_url) {
      Object.assign(draft, {
        app_url: latest.app_url || "",
        install_url: latest.install_url || "",
        scopes: [...(latest.scopes || [])],
        scope_justifications: { ...(latest.scope_justifications || {}) },
        capabilities: [...(latest.capabilities || [])]
      });
      redirectText.value = (latest.redirect_uris || []).join("\n");
      eventsText.value = latest.events?.length ? JSON.stringify(latest.events, null, 2) : "";
    }
  },
  { immediate: true }
);

function versionLabel(status) {
  return te(`versions.status.${status}`) ? t(`versions.status.${status}`) : status;
}
function versionBadge(status) {
  return VERSION_BADGE[status] || "";
}
function submittable(v) {
  return SUBMITTABLE.includes(v.status) && props.app.status !== "archived";
}

function blockers(v) {
  const list = [];
  if (!session.totpEnabled) {
    list.push({ key: "totp", text: t("submit.blockers.totp"), to: "/security", action: t("submit.fixTotp") });
  }
  const weak = weakJustifications(v);
  if (weak.length) {
    list.push({ key: "just", text: t("submit.blockers.justifications", { scopes: weak.join(", "), min: MIN_JUSTIFICATION }) });
  }
  const gaps = listingGaps(props.listingEn);
  if (gaps.length) {
    const fields = gaps.map((f) => t(`listing.fields.${f}`)).join(", ");
    list.push({ key: "listing", text: t("submit.blockers.listing", { fields }), tab: "listing", action: t("submit.fixListing") });
  }
  if (session.developer?.status === "blocked") list.push({ key: "provider", text: t("submit.blockers.provider") });
  if (["blocked", "suspended"].includes(props.app.status)) list.push({ key: "app", text: t("submit.blockers.app") });
  return list;
}

const justificationLength = (scope) => String(draft.scope_justifications[scope] || "").trim().length;
const justificationShort = (scope) => justificationLength(scope) < MIN_JUSTIFICATION;
const redirectUris = computed(() => redirectText.value.split("\n").map((l) => l.trim()).filter(Boolean));
// The hub's manifest check judges the declarations; here only the JSON shape is checked.
const parsedEvents = computed(() => {
  const text = eventsText.value.trim();
  if (!text) return { events: [] };
  try {
    const events = JSON.parse(text);
    return Array.isArray(events) ? { events } : { error: t("editor.eventsNotArray") };
  } catch (e) {
    return { error: t("editor.eventsInvalid", { reason: e.message }) };
  }
});
const eventsError = computed(() => parsedEvents.value.error || "");
const canCreate = computed(
  () =>
    !!draft.version &&
    !!draft.app_url &&
    !!draft.install_url &&
    redirectUris.value.length > 0 &&
    draft.scopes.length > 0 &&
    !eventsError.value
);

async function run(fn) {
  busy.value = true;
  error.value = null;
  submittedNote.value = false;
  try {
    await fn();
    emit("changed");
  } catch (e) {
    error.value = { text: errorText(e), list: e.errors };
  } finally {
    busy.value = false;
  }
}

const createVersion = () => run(async () => {
  const justifications = Object.fromEntries(draft.scopes.map((s) => [s, String(draft.scope_justifications[s] || "").trim()]));
  await api.createVersion(props.app.id, {
    version: draft.version,
    app_url: draft.app_url,
    install_url: draft.install_url,
    redirect_uris: redirectUris.value,
    scopes: draft.scopes,
    scope_justifications: justifications,
    capabilities: draft.capabilities,
    events: parsedEvents.value.events
  });
  draft.version = "";
});

const submit = (v) => run(async () => {
  await api.submitVersion(props.app.id, v.id);
  selectedId.value = v.id;
  submittedNote.value = true;
});
</script>

<style scoped>
.editor__group { margin: var(--space-5) 0 var(--space-2); font-size: var(--text-sm); color: var(--ink-muted); text-transform: uppercase; letter-spacing: .04em; }
.scope { padding: var(--space-3) 0; border-bottom: 1px solid var(--line); }
.scope__check { display: flex; gap: var(--space-2); align-items: flex-start; margin-bottom: var(--space-2); font-size: var(--text-sm); }
.scope__check input { margin-top: 4px; accent-color: var(--accent); }
.scope__check code { margin-left: var(--space-1); }
</style>
